import { huge, type HugeNumber, type HugeNumberInput } from "@/shared/numbers";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationDefinition,
  type ExplorationMpSlotDefinition,
  type ExplorationMpSlotId,
} from "./explorationTypes";

export const EXPLORATION_MP_SLOT_SEQUENCE = [
  { id: "hov-1", biomeId: "hov", amount: 1 },
  { id: "tim-1", biomeId: "tim", amount: 1 },
  { id: "mat-1", biomeId: "mat", amount: 1 },
  { id: "lev-1", biomeId: "lev", amount: 1 },
  { id: "hov-3", biomeId: "hov", amount: 3 },
  { id: "tim-3", biomeId: "tim", amount: 3 },
  { id: "mat-3", biomeId: "mat", amount: 3 },
  { id: "lev-3", biomeId: "lev", amount: 3 },
  { id: "hov-6", biomeId: "hov", amount: 6 },
  { id: "tim-6", biomeId: "tim", amount: 6 },
  { id: "mat-6", biomeId: "mat", amount: 6 },
  { id: "lev-6", biomeId: "lev", amount: 6 },
] as const satisfies readonly ExplorationMpSlotDefinition[];

const EXPLORATION_MP_SLOT_IDS = new Set<ExplorationMpSlotId>(
  EXPLORATION_MP_SLOT_SEQUENCE.map((slot) => slot.id),
);

const EXPLORATION_PROVISIONAL_PRICE_BASE_EXPONENT = 10;
const EXPLORATION_PROVISIONAL_PRICE_EXPONENT_STEP = 3;

export const EXPLORATION_PROVISIONAL_TAB_UNLOCK_COST = huge("1e10");
export const EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS = 1_000;
export const EXPLORATION_TIM_ACCELERATION_TIM_PER_GAME_SECOND = 0.1;
export const EXPLORATION_TIM_ACCELERATION_SPEED_MULTIPLIER = 3;

export function resolveExplorationMpSlotCost(
  purchaseNumber: number,
): HugeNumber {
  if (!Number.isSafeInteger(purchaseNumber) || purchaseNumber < 1) {
    throw new RangeError("purchaseNumber must be a positive safe integer.");
  }
  const exponent =
    EXPLORATION_PROVISIONAL_PRICE_BASE_EXPONENT +
    (EXPLORATION_PROVISIONAL_PRICE_EXPONENT_STEP *
      purchaseNumber *
      (purchaseNumber - 1)) /
      2;
  return huge(`1e${exponent}`);
}

export interface CreateExplorationDefinitionOptions {
  readonly mpSlotCosts?: readonly (HugeNumberInput | null)[];
}

export function createExplorationDefinition(
  options: CreateExplorationDefinitionOptions = {},
): ExplorationDefinition {
  return {
    tabUnlockCost: EXPLORATION_PROVISIONAL_TAB_UNLOCK_COST,
    areas: {
      hov: {
        id: "hov",
        periodMs: 10_000,
        resolveEffect: (biomeLevel) => ({
          type: "award-ohov-power",
          amount: biomeLevel,
        }),
      },
      tim: {
        id: "tim",
        periodMs: 20_000,
        resolveEffect: (biomeLevel) => ({
          type: "add-exploration-time",
          milliseconds: 100 * biomeLevel,
        }),
      },
      mat: {
        id: "mat",
        periodMs: 60_000,
        resolveEffect: (biomeLevel) => ({
          type: "multiply-hg",
          multiplier: huge(158).pow(biomeLevel),
        }),
      },
      lev: {
        id: "lev",
        periodMs: 111_000,
        resolveEffect: () => ({
          type: "raise-biome-level",
          amount: 1,
        }),
      },
    },
    mpSlots: EXPLORATION_MP_SLOT_SEQUENCE,
    mpSlotCosts: normalizeCosts(
      options.mpSlotCosts ??
        EXPLORATION_MP_SLOT_SEQUENCE.map((_, index) =>
          resolveExplorationMpSlotCost(index + 1),
        ),
    ),
    timAcceleration: {
      unlockCumulativeTimAddedMs:
        EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS,
      timPerGameSecond: EXPLORATION_TIM_ACCELERATION_TIM_PER_GAME_SECOND,
      speedMultiplier: EXPLORATION_TIM_ACCELERATION_SPEED_MULTIPLIER,
    },
  };
}

export const DEFAULT_EXPLORATION_DEFINITION = createExplorationDefinition();

function normalizeCosts(
  values: readonly (HugeNumberInput | null)[] | undefined,
): readonly (HugeNumber | null)[] {
  if (values === undefined) return [];
  return values.map((value) => {
    if (value === null) return null;
    const cost = huge(value);
    return cost.isFinite() && cost.gt(0) ? cost : null;
  });
}

export function isExplorationBiomeId(
  value: unknown,
): value is (typeof EXPLORATION_BIOME_IDS)[number] {
  return (
    typeof value === "string" &&
    EXPLORATION_BIOME_IDS.includes(
      value as (typeof EXPLORATION_BIOME_IDS)[number],
    )
  );
}

export function isExplorationMpSlotId(
  value: unknown,
): value is ExplorationMpSlotId {
  return (
    typeof value === "string" &&
    EXPLORATION_MP_SLOT_IDS.has(value as ExplorationMpSlotId)
  );
}

export function getExplorationMpSlotDefinition(
  slotId: ExplorationMpSlotId,
): ExplorationMpSlotDefinition {
  const slot = EXPLORATION_MP_SLOT_SEQUENCE.find(
    (candidate) => candidate.id === slotId,
  );
  if (slot === undefined) {
    throw new Error(`Unknown Exploration MP slot: ${slotId}`);
  }
  return slot;
}
